/**
 * EditorBridge — postMessage bridge to the embedded OpenCut video editor iframe.
 *
 * Usage:
 *   1. VideoEditorPage registers the iframe ref on mount: editorBridge.setIframe(ref)
 *   2. Any caller (e.g. ResultDisplay "Edit in video editor" button) enqueues a
 *      project payload: editorBridge.loadProject(payload)
 *   3. If the editor is already ready, payload is posted immediately.
 *      Otherwise it is queued and flushed when the editor posts AIGOD_EDITOR_READY.
 */

export interface EditorMediaItem {
  type: 'video' | 'audio' | 'subtitles' | 'image';
  url: string;
  duration_s?: number;
  language?: string;
  format?: string;
}

export interface EditorTimelineClip {
  track: number;
  start_s: number;
  media_index: number;
  duration_s?: number;
}

export interface EditorTimelineHint {
  clips: EditorTimelineClip[];
}

export interface EditorLoadPayload {
  project_id: string;
  source_job_id: string;
  media: EditorMediaItem[];
  timeline_hint?: EditorTimelineHint;
}

class EditorBridge {
  private iframe: HTMLIFrameElement | null = null;
  private ready = false;
  private pending: EditorLoadPayload | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('message', this.onMessage);
    }
  }

  private onMessage = (event: MessageEvent) => {
    const data = event.data;
    if (!data || typeof data !== 'object') return;

    if (data.type === 'AIGOD_EDITOR_READY') {
      this.ready = true;
      this.flush();
    }
  };

  setIframe(iframe: HTMLIFrameElement | null): void {
    this.iframe = iframe;
    if (!iframe) {
      this.ready = false;
    }
  }

  loadProject(payload: EditorLoadPayload): void {
    if (this.ready && this.iframe?.contentWindow) {
      this.iframe.contentWindow.postMessage({ type: 'AIGOD_LOAD_PROJECT', payload }, '*');
      return;
    }
    this.pending = payload;
  }

  private flush(): void {
    if (!this.pending || !this.iframe?.contentWindow) return;
    this.iframe.contentWindow.postMessage(
      { type: 'AIGOD_LOAD_PROJECT', payload: this.pending },
      '*'
    );
    this.pending = null;
  }
}

export const editorBridge = new EditorBridge();
